import React, { useState, Fragment, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@material-ui/core/Grid";
import Box from "@mui/material/Box";
import DashboardMap from "../DashboardMap";
import DashboardListContainer from "../DashboardListContainer";
import { getNotifications } from "redux/actions/notificationActions";
import { getBusesList } from "redux/actions/busesActions";
import { getDriversList } from "redux/actions/driversActions";
import { getRoutesList } from "redux/actions/routesActions";
import { formattedCardListData } from "../../utils/utils";
import useStyles from "./styles";

//Footer Component

const DashboardContainer = (props) => {
  const { dashboardContent, notificationSection } = useStyles();

  const dispatch = useDispatch();

  const notificationsListData = useSelector(
    (state) => state.notifications.notificationsData
  );

  const busesListData = useSelector((state) => state.buses.busesList);

  const driversListData = useSelector((state) => state.drivers.driversList);

  const routesListData = useSelector((state) => state.routes.routesList);

  const notifcationChipButtons = useMemo(() => [
    { label: "Events", count: "5", focused: false, color: "green", tabIndex: 0 },
    { label: "Incidents", count: "5", focused: true, color: "red", tabIndex: 0 },
    { label: "Ops Alerts", count: "5", focused: false, color: "orange", tabIndex: 0 },
  ], []);

  const routeChipButtons = useMemo(() => [
    { label: "Active Routes", count: "6", focused: true, color: "blue", tabIndex: 3 },
    { label: "Completed Routes", count: "3", focused: false, color: "orange", tabIndex: 3 },
  ], []);
  const [chipButtons, setChipButtons] = useState(notifcationChipButtons);

  const [coOrdinates, setCoOrdinates] = useState();
  const [dataList, setDataList] = useState(notificationsListData);
  const [cardList, setCardList] = useState({});
  const [chipButtonTabIndex, setChipButtonTabIndex] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const [type, setType] = useState("");
  const [expandListFromMap, setExpandListFromMap] = useState(false);
  const [indexFromMap, setIndexFromMap] = useState(null);
  const [datafromList, setDatafromList] = useState(null);
  const [activeBusType, setActiveBusType] = useState('green');
  const [isManualChipSelection, setIsManualChipSelection] = useState(false);

  const getFormattedChipButtonsData = useCallback((list, chipBtnIndex) => {
    for (const [index] of list.entries()) {
      // Preserve existing tabIndex if it exists, otherwise use current tabIndex
      if (!list[index].hasOwnProperty('tabIndex')) {
        list[index].tabIndex = tabIndex;
      }
      if (chipBtnIndex === index) list[index].focused = true;
      else list[index].focused = false;
    }
    return list;
  }, [tabIndex]);

  useEffect(() => {
    dispatch(getNotifications());
    dispatch(getBusesList());
    dispatch(getDriversList());
    dispatch(getRoutesList());
  }, [dispatch]);

  useEffect(() => {
    switch (tabIndex) {
      case 0:
        setDataList(notificationsListData);
        const notificationChips = notifcationChipButtons.map((item, index) => ({
          ...item,
          tabIndex: tabIndex,
          focused: index === 0,
        }));
        setChipButtons(notificationChips);
        setType("events");
        break;
      case 1:
        setDataList(busesListData);
        setChipButtons([]);
        setType("");
        break;
      case 2:
        setDataList(driversListData);
        setChipButtons([]);
        setType("");
        break;
      case 3:
        // Only set up routes if not already done by handleTabIndex or handleChipButton
        if (!isManualChipSelection && (!type || (type !== "activeRoutes" && type !== "completedRoutes"))) {
          setChipButtonTabIndex(0);
          setDataList(routesListData);
          const routeChips = getFormattedChipButtonsData(routeChipButtons, 0);
          setChipButtons(routeChips);
          setType("activeRoutes");
        }
        break;
      default:
        break;
    }
  }, [
    tabIndex,
    notificationsListData,
    busesListData,
    driversListData,
    routesListData,
    notifcationChipButtons,
    routeChipButtons,
    getFormattedChipButtonsData,
    type,
    isManualChipSelection,
  ]);

  useEffect(() => {
    if (!dataList) {
      setCardList({});
      return;
    }

    let newCardList;
    if (tabIndex === 0) {
      newCardList = formattedCardListData(tabIndex, dataList, type);
    } else if (tabIndex === 3) {
      if (type === "completedRoutes") {
        const completedData = formattedCardListData(
          tabIndex,
          dataList,
          "completedRoutes"
        );
        newCardList = Array.isArray(completedData)
          ? completedData.slice(0, 3)
          : completedData;
      } else if (type === "activeRoutes") {
        // Show active routes data
        newCardList = formattedCardListData(tabIndex, dataList, "activeRoutes");
      } else {
        // Default fallback for Routes tab when type is not set
        newCardList = formattedCardListData(tabIndex, dataList, "activeRoutes");
      }
    } else {
      newCardList = formattedCardListData(tabIndex, dataList);
    }
    setCardList(newCardList);
  }, [dataList, type, tabIndex]);

  // Additional effect to ensure Routes tab shows Active Routes initially only when no type is set
  useEffect(() => {
    if (tabIndex === 3 && routesListData && type === "") {
      setType("activeRoutes");
      const activeRoutesData = formattedCardListData(3, routesListData, "activeRoutes");
      setCardList(activeRoutesData);
    }
  }, [tabIndex, routesListData, type]);

  const handleClickDrawRoute = (data) => {
    if (coOrdinates && data && 
        ((coOrdinates.id && data.id && coOrdinates.id === data.id) || 
         (coOrdinates.busNo && data.busNo && coOrdinates.busNo === data.busNo) ||
         (coOrdinates === data))) {
        setCoOrdinates(null);
        setExpandListFromMap(false);
    } else {
        setCoOrdinates(data);
    }
    // Preserve the current type (activeRoutes or completedRoutes)
    // Don't reset the type when drawing routes
  };

  const handleTabIndex = (index) => {
    const previousTabIndex = tabIndex;
    setTabIndex(index);
    if (index === 3) {
      // Only set up active routes as default when coming from a different tab
      if (previousTabIndex !== 3) {
        setIsManualChipSelection(false);
        setType("activeRoutes");
        setChipButtonTabIndex(0);
        setDataList(routesListData);
        // Ensure the Active Routes chip is focused
        const routeChips = getFormattedChipButtonsData(routeChipButtons, 0);
        setChipButtons(routeChips);
        // Immediately set the card list for active routes
        if (routesListData) {
          const activeRoutesData = formattedCardListData(3, routesListData, "activeRoutes");
          setCardList(activeRoutesData);
        }
      }
      // If already in Routes tab, preserve current state
    } else {
      setIsManualChipSelection(false);
      setType("");
      setChipButtonTabIndex(0);
    }
  };

  const handleSetActiveBusType = (busType) => {
    setActiveBusType(busType);
  };

  const handleChipButton = (data, index) => {
    setCoOrdinates("");
    if (data) {
  // Ignore clicks on disabled chips (e.g., Completed Routes)
  if (data.disabled) return;
      if (data.tabIndex === 0) {
        setChipButtons(
          getFormattedChipButtonsData(notifcationChipButtons, index)
        );
        switch (data.label) {
          case "Events":
            setActiveBusType('green');
            setCardList(formattedCardListData(tabIndex, dataList, "events"));
            setChipButtonTabIndex(0);
            break;
          case "Incidents":
            setActiveBusType('red')
            setCardList(formattedCardListData(tabIndex, dataList, "incidents"));
            setChipButtonTabIndex(1);
            break;
          case "Ops Alerts":
            setActiveBusType('orange')
            setCardList(formattedCardListData(tabIndex, dataList, "oprAlerts"));
            setChipButtonTabIndex(2);
            break;
          default:
            break;
        }
      } else if (data.tabIndex === 3) {
        setIsManualChipSelection(true);
        setChipButtons(getFormattedChipButtonsData(routeChipButtons, index));
        switch (data.label) {
          case "Active Routes":
            setCardList(
              formattedCardListData(tabIndex, dataList, "activeRoutes")
            );
            setType("activeRoutes");
            break;
          case "Completed Routes":
            const completedData = formattedCardListData(tabIndex, dataList, "completedRoutes");
            setCardList(Array.isArray(completedData) ? completedData.slice(0, 6) : completedData);
            setType("completedRoutes");
            break;
          default:
            break;
        }
      }
    }
  };

  const handleExpandingList = (index) => {
    setIndexFromMap(index);
    setExpandListFromMap(!expandListFromMap);
    // When popup is closed (index === null), also clear the datafromList
    if (index === null) {
      setDatafromList(null);
    }
  };

  const handleClickFromListToMap = (data) => {
    if (data !== null) {
      setDatafromList(data);
    } else {
      setDatafromList(null);
    }
  };

  return (
    <Fragment>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container className={dashboardContent}>
          <Grid item xs={12} sm={12} md={8} lg={9} xl={9}>
            <DashboardMap
              activeBusType={activeBusType}
              setActiveBusType={handleSetActiveBusType}
              coOrdinates={coOrdinates}
              handleClickDrawRoute={handleClickDrawRoute}
              cardList={cardList}
              tabIndex={tabIndex}
              type={type}
              handleExpandingList={handleExpandingList}
              zoom={11}
              datafromList={datafromList}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={4}
            lg={3}
            xl={3}
            className={notificationSection}
          >
            <DashboardListContainer
              handleClickDrawRoute={handleClickDrawRoute}
              handleClickFromListToMap={handleClickFromListToMap}
              cardList={cardList}
              handleTabIndex={handleTabIndex}
              handleChipButton={handleChipButton}
              chipButtons={chipButtons}
              coOrdinates={coOrdinates}
              expandListFromMap={expandListFromMap}
              indexFromMap={indexFromMap}
              chipButtonTabIndex={chipButtonTabIndex}
            />
          </Grid>
        </Grid>
      </Box>
    </Fragment>
  );
};

export default DashboardContainer;

// import React, { useState, Fragment, useEffect, useCallback, useMemo } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import Grid from "@material-ui/core/Grid";
// import Box from "@mui/material/Box";
// import DashboardMap from "../DashboardMap";
// import DashboardListContainer from "../DashboardListContainer";
// import { getNotifications } from "redux/actions/notificationActions";
// import { getBusesList } from "redux/actions/busesActions";
// import { getDriversList } from "redux/actions/driversActions";
// import { getRoutesList } from "redux/actions/routesActions";
// import { formattedCardListData } from "../../utils/utils";
// import useStyles from "./styles";

// const DashboardContainer = (props) => {
//   const { dashboardContent, notificationSection } = useStyles();

//   const dispatch = useDispatch();

//   const notificationsListData = useSelector(
//     (state) => state.notifications.notificationsData
//   );

//   const busesListData = useSelector((state) => state.buses.busesList);
//   const driversListData = useSelector((state) => state.drivers.driversList);
//   const routesListData = useSelector((state) => state.routes.routesList);

//   const notifcationChipButtons = useMemo(() => [
//     { label: "Events", count: "5", focused: true, color: "green", tabIndex: 0 },
//     { label: "Incidents", count: "5", focused: false, color: "red", tabIndex: 0 },
//     { label: "Opr.Alerts", count: "5", focused: false, color: "orange", tabIndex: 0 },
//   ], []);

//   const routeChipButtons = useMemo(() => [
//     { label: "Active Routes", count: "6", focused: true, color: "blue", tabIndex: 3 },
//     { label: "Completed Routes", count: "6", focused: false, color: "orange", tabIndex: 3 },
//   ], []);

//   const [chipButtons, setChipButtons] = useState(notifcationChipButtons);
//   const [coOrdinates, setCoOrdinates] = useState();
//   const [cardList, setCardList] = useState([]); // Initialize as empty array
//   const [chipButtonTabIndex, setChipButtonTabIndex] = useState(0);
//   const [tabIndex, setTabIndex] = useState(0);
//   const [type, setType] = useState("");
//   const [expandListFromMap, setExpandListFromMap] = useState(false);
//   const [indexFromMap, setIndexFromMap] = useState(null);
//   const [datafromList, setDatafromList] = useState(null);
//   const [currentFilter, setCurrentFilter] = useState("events");

//   useEffect(() => {
//     dispatch(getNotifications());
//     dispatch(getBusesList());
//     dispatch(getDriversList());
//     dispatch(getRoutesList());
//   }, [dispatch]);

//   // Get the appropriate data list based on tab index
//   const getCurrentDataList = useCallback(() => {
//     switch (tabIndex) {
//       case 0:
//         return notificationsListData || [];
//       case 1:
//         return busesListData || [];
//       case 2:
//         return driversListData || [];
//       case 3:
//         return routesListData || [];
//       default:
//         return [];
//     }
//   }, [tabIndex, notificationsListData, busesListData, driversListData, routesListData]);

//   useEffect(() => {
//     const dataList = getCurrentDataList();
    
//     if (tabIndex === 0) {
//       setChipButtons(notifcationChipButtons);
//       setCurrentFilter("events");
//     } else if (tabIndex === 3) {
//       setChipButtons(routeChipButtons);
//       setCurrentFilter("activeRoutes");
//     } else {
//       setChipButtons([]);
//     }
//   }, [tabIndex, notifcationChipButtons, routeChipButtons, getCurrentDataList]);

//   useEffect(() => {
//     const dataList = getCurrentDataList();
    
//     if (dataList && dataList.length > 0) {
//       let formattedData;
      
//       if (tabIndex === 0) {
//         formattedData = formattedCardListData(tabIndex, dataList, currentFilter);
//       } else if (tabIndex === 3) {
//         formattedData = formattedCardListData(tabIndex, dataList, currentFilter);
//       } else {
//         formattedData = formattedCardListData(tabIndex, dataList);
//       }
      
//       console.log("Formatted card data:", formattedData);
//       setCardList(formattedData || []);
//     } else {
//       console.log("No data available for tab:", tabIndex);
//       setCardList([]);
//     }
//   }, [tabIndex, currentFilter, getCurrentDataList]);

//   const handleClickDrawRoute = (data) => {
//     setCoOrdinates(data);
//     setType("");
//   };

//   const handleTabIndex = (index) => {
//     setTabIndex(index);
//     setCoOrdinates(null);
//     setDatafromList(null);
//     setCardList([]); // Clear card list temporarily
//   };

//   const getFormattedChipButtonsData = useCallback((list, chipBtnIndex) => {
//     return list.map((item, index) => ({
//       ...item,
//       focused: chipBtnIndex === index
//     }));
//   }, []);

//   const handleChipButton = (data, index) => {
//     setCoOrdinates(null);
//     setDatafromList(null);
    
//     if (data && data.tabIndex === 0) {
//       const updatedChipButtons = getFormattedChipButtonsData(notifcationChipButtons, index);
//       setChipButtons(updatedChipButtons);
      
//       let filterType;
//       switch (data.label) {
//         case "Events":
//           filterType = "events";
//           setChipButtonTabIndex(0);
//           break;
//         case "Incidents":
//           filterType = "incidents";
//           setChipButtonTabIndex(1);
//           break;
//         case "Opr.Alerts":
//           filterType = "oprAlerts";
//           setChipButtonTabIndex(2);
//           break;
//         default:
//           filterType = "events";
//       }
//       setCurrentFilter(filterType);
      
//     } else if (data && data.tabIndex === 3) {
//       const updatedChipButtons = getFormattedChipButtonsData(routeChipButtons, index);
//       setChipButtons(updatedChipButtons);
      
//       let filterType;
//       switch (data.label) {
//         case "Active Routes":
//           filterType = "activeRoutes";
//           break;
//         case "Completed Routes":
//           filterType = "completedRoutes";
//           setType("completedRoutes");
//           break;
//         default:
//           filterType = "activeRoutes";
//       }
//       setCurrentFilter(filterType);
//     }
//   };

//   const handleExpandingList = (index) => {
//     setIndexFromMap(index);
//     setExpandListFromMap(!expandListFromMap);
//   };

//   const handleClickFromListToMap = (data) => {
//     setDatafromList(data);
//   };

//   // Add loading state or debug info
//   console.log("Current tab:", tabIndex);
//   console.log("Current filter:", currentFilter);
//   console.log("Card list length:", cardList.length);
//   console.log("Notifications data:", notificationsListData);
//   console.log("Buses data:", busesListData);
//   console.log("Drivers data:", driversListData);
//   console.log("Routes data:", routesListData);

//   return (
//     <Fragment>
//       <Box sx={{ flexGrow: 1 }}>
//         <Grid container className={dashboardContent}>
//           <Grid item xs={12} sm={12} md={8} lg={9} xl={9}>
//             <DashboardMap
//               coOrdinates={coOrdinates}
//               handleClickDrawRoute={handleClickDrawRoute}
//               cardList={cardList}
//               tabIndex={tabIndex}
//               type={type}
//               handleExpandingList={handleExpandingList}
//               zoom={10}
//               datafromList={datafromList}
//             />
//           </Grid>
//           <Grid
//             item
//             xs={12}
//             sm={12}
//             md={4}
//             lg={3}
//             xl={3}
//             className={notificationSection}
//           >
//             <DashboardListContainer
//               handleClickDrawRoute={handleClickDrawRoute}
//               handleClickFromListToMap={handleClickFromListToMap}
//               cardList={cardList}
//               handleTabIndex={handleTabIndex}
//               handleChipButton={handleChipButton}
//               chipButtons={chipButtons}
//               coOrdinates={coOrdinates}
//               expandListFromMap={expandListFromMap}
//               indexFromMap={indexFromMap}
//               chipButtonTabIndex={chipButtonTabIndex}
//             />
//           </Grid>
//         </Grid>
//       </Box>
//     </Fragment>
//   );
// };

// export default DashboardContainer;



















// import React, { useState, Fragment, useEffect, useCallback, useMemo } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import Grid from "@material-ui/core/Grid";
// import Box from "@mui/material/Box";
// import DashboardMap from "../DashboardMap";
// import DashboardListContainer from "../DashboardListContainer";
// import { getNotifications } from "redux/actions/notificationActions";
// import { getBusesList } from "redux/actions/busesActions";
// import { getDriversList } from "redux/actions/driversActions";
// import { getRoutesList } from "redux/actions/routesActions";
// import { formattedCardListData } from "../../utils/utils";
// import useStyles from "./styles";

// const DashboardContainer = (props) => {
//   const { dashboardContent, notificationSection } = useStyles();

//   const dispatch = useDispatch();

//   const notificationsListData = useSelector(
//     (state) => state.notifications.notificationsData
//   );

//   const busesListData = useSelector((state) => state.buses.busesList);
//   const driversListData = useSelector((state) => state.drivers.driversList);
//   const routesListData = useSelector((state) => state.routes.routesList);

//   const notifcationChipButtons = useMemo(() => [
//     { label: "Events", count: "5", focused: true, color: "green", tabIndex: 0 },
//     { label: "Incidents", count: "5", focused: false, color: "red", tabIndex: 0 },
//     { label: "Opr.Alerts", count: "5", focused: false, color: "orange", tabIndex: 0 },
//   ], []);

//   const routeChipButtons = useMemo(() => [
//     { label: "Active Routes", count: "6", focused: true, color: "blue", tabIndex: 3 },
//     { label: "Completed Routes", count: "6", focused: false, color: "orange", tabIndex: 3 },
//   ], []);

//   const [chipButtons, setChipButtons] = useState(notifcationChipButtons);
//   const [coOrdinates, setCoOrdinates] = useState();
//   const [cardList, setCardList] = useState([]); // Initialize as empty array
//   const [chipButtonTabIndex, setChipButtonTabIndex] = useState(0);
//   const [tabIndex, setTabIndex] = useState(0);
//   const [type, setType] = useState("");
//   const [expandListFromMap, setExpandListFromMap] = useState(false);
//   const [indexFromMap, setIndexFromMap] = useState(null);
//   const [datafromList, setDatafromList] = useState(null);
//   const [currentFilter, setCurrentFilter] = useState("events");

//   useEffect(() => {
//     dispatch(getNotifications());
//     dispatch(getBusesList());
//     dispatch(getDriversList());
//     dispatch(getRoutesList());
//   }, [dispatch]);

//   // Get the appropriate data list based on tab index
//   const getCurrentDataList = useCallback(() => {
//     switch (tabIndex) {
//       case 0:
//         return notificationsListData || [];
//       case 1:
//         return busesListData || [];
//       case 2:
//         return driversListData || [];
//       case 3:
//         return routesListData || [];
//       default:
//         return [];
//     }
//   }, [tabIndex, notificationsListData, busesListData, driversListData, routesListData]);

//   useEffect(() => {
//     const dataList = getCurrentDataList();
    
//     if (tabIndex === 0) {
//       setChipButtons(notifcationChipButtons);
//       setCurrentFilter("events");
//     } else if (tabIndex === 3) {
//       setChipButtons(routeChipButtons);
//       setCurrentFilter("activeRoutes");
//     } else {
//       setChipButtons([]);
//     }
//   }, [tabIndex, notifcationChipButtons, routeChipButtons, getCurrentDataList]);

//   useEffect(() => {
//     const dataList = getCurrentDataList();
    
//     if (dataList && dataList.length > 0) {
//       let formattedData;
      
//       if (tabIndex === 0) {
//         formattedData = formattedCardListData(tabIndex, dataList, currentFilter);
//       } else if (tabIndex === 3) {
//         formattedData = formattedCardListData(tabIndex, dataList, currentFilter);
//       } else {
//         formattedData = formattedCardListData(tabIndex, dataList);
//       }
      
//       console.log("Formatted card data:", formattedData);
//       setCardList(formattedData || []);
//     } else {
//       console.log("No data available for tab:", tabIndex);
//       setCardList([]);
//     }
//   }, [tabIndex, currentFilter, getCurrentDataList]);

//   const handleClickDrawRoute = (data) => {
//     setCoOrdinates(data);
//     setType("");
//   };

//   const handleTabIndex = (index) => {
//     setTabIndex(index);
//     setCoOrdinates(null);
//     setDatafromList(null);
//     setCardList([]); // Clear card list temporarily
//   };

//   const getFormattedChipButtonsData = useCallback((list, chipBtnIndex) => {
//     return list.map((item, index) => ({
//       ...item,
//       focused: chipBtnIndex === index
//     }));
//   }, []);

//   const handleChipButton = (data, index) => {
//     setCoOrdinates(null);
//     setDatafromList(null);
    
//     if (data && data.tabIndex === 0) {
//       const updatedChipButtons = getFormattedChipButtonsData(notifcationChipButtons, index);
//       setChipButtons(updatedChipButtons);
      
//       let filterType;
//       switch (data.label) {
//         case "Events":
//           filterType = "events";
//           setChipButtonTabIndex(0);
//           break;
//         case "Incidents":
//           filterType = "incidents";
//           setChipButtonTabIndex(1);
//           break;
//         case "Opr.Alerts":
//           filterType = "oprAlerts";
//           setChipButtonTabIndex(2);
//           break;
//         default:
//           filterType = "events";
//       }
//       setCurrentFilter(filterType);
      
//     } else if (data && data.tabIndex === 3) {
//       const updatedChipButtons = getFormattedChipButtonsData(routeChipButtons, index);
//       setChipButtons(updatedChipButtons);
      
//       let filterType;
//       switch (data.label) {
//         case "Active Routes":
//           filterType = "activeRoutes";
//           break;
//         case "Completed Routes":
//           filterType = "completedRoutes";
//           setType("completedRoutes");
//           break;
//         default:
//           filterType = "activeRoutes";
//       }
//       setCurrentFilter(filterType);
//     }
//   };

//   const handleExpandingList = (index) => {
//     setIndexFromMap(index);
//     setExpandListFromMap(!expandListFromMap);
//   };

//   const handleClickFromListToMap = (data) => {
//     setDatafromList(data);
//   };

//   // Add loading state or debug info
//   console.log("Current tab:", tabIndex);
//   console.log("Current filter:", currentFilter);
//   console.log("Card list length:", cardList.length);
//   console.log("Notifications data:", notificationsListData);
//   console.log("Buses data:", busesListData);
//   console.log("Drivers data:", driversListData);
//   console.log("Routes data:", routesListData);

//   return (
//     <Fragment>
//       <Box sx={{ flexGrow: 1 }}>
//         <Grid container className={dashboardContent}>
//           <Grid item xs={12} sm={12} md={8} lg={9} xl={9}>
//             <DashboardMap
//               coOrdinates={coOrdinates}
//               handleClickDrawRoute={handleClickDrawRoute}
//               cardList={cardList}
//               tabIndex={tabIndex}
//               type={type}
//               handleExpandingList={handleExpandingList}
//               zoom={10}
//               datafromList={datafromList}
//             />
//           </Grid>
//           <Grid
//             item
//             xs={12}
//             sm={12}
//             md={4}
//             lg={3}
//             xl={3}
//             className={notificationSection}
//           >
//             <DashboardListContainer
//               handleClickDrawRoute={handleClickDrawRoute}
//               handleClickFromListToMap={handleClickFromListToMap}
//               cardList={cardList}
//               handleTabIndex={handleTabIndex}
//               handleChipButton={handleChipButton}
//               chipButtons={chipButtons}
//               coOrdinates={coOrdinates}
//               expandListFromMap={expandListFromMap}
//               indexFromMap={indexFromMap}
//               chipButtonTabIndex={chipButtonTabIndex}
//             />
//           </Grid>
//         </Grid>
//       </Box>
//     </Fragment>
//   );
// };

// export default DashboardContainer;